import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import Title from '../layouts/Title';
import ProjectsCard from './ProjectsCard';
import { useTranslation } from 'react-i18next';
import { fetchProjects } from '../../api';
import { queryKeys } from '../../api/keys';

const Projects = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 6;

  // ✅ إضافة ref للإشارة إلى قسم المشاريع
  const sectionRef = useRef(null);

  const { data: responseData, isLoading, error } = useQuery({
    queryKey: queryKeys.projects(currentPage, perPage, currentLang),
    queryFn: () => fetchProjects(currentPage, perPage, currentLang),
    keepPreviousData: true,
  });

  const projects = responseData?.data || [];
  const pagination = responseData?.pagination;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination?.last_page) {
      setCurrentPage(newPage);
      // ✅ التمرير إلى بداية قسم المشاريع بدلاً من أعلى الصفحة
      if (sectionRef.current) {
        sectionRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // (اختياري) إذا أردت التمرير عند تحميل الصفحة الأولى لأول مرة
  useEffect(() => {
    if (sectionRef.current && window.location.hash === '#projects') {
      sectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  if (isLoading) {
    return (
      <section className="w-full py-20 border-b-[1px] border-b-black">
        <div className="flex justify-center items-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-designColor"></div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="w-full py-20 border-b-[1px] border-b-black">
        <p className="text-red-500 text-center">Error: {error.message}</p>
      </section>
    );
  }

  if (!projects.length && pagination?.total === 0) {
    return (
      <section className="w-full py-20 border-b-[1px] border-b-black">
        <p className="text-gray-400 text-center">{t("Projects.noProjects") || "No projects yet."}</p>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}  // ✅ ربط الـ ref مع عنصر الـ section
      id="projects"
      className="w-full py-20 border-b-[1px] border-b-black"
      style={{ direction: i18n.language === 'ar' ? 'rtl' : 'ltr' }}
    >
      <div className="flex justify-center items-center text-center">
        <Title title={t("Projects.feature")} des={t("Projects.title")} />
      </div>

      {/* شبكة المشاريع */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 xl:gap-14">
        {projects.map((project) => (
          <ProjectsCard key={project.id} project={project} />
        ))}
      </div>

      {/* عناصر التحكم في التصفح */}
      {pagination && pagination.last_page > 1 && (
        <div className="flex justify-center items-center gap-3 mt-12 flex-wrap">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded-lg transition-all duration-300 ${
              currentPage === 1
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-[#1e2024] text-gray-300 hover:bg-designColor hover:text-white'
            }`}
          >
            {t("pagination.previous") || "←"}
          </button>

          <div className="flex gap-2 flex-wrap justify-center">
            {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`w-10 h-10 rounded-lg transition-all duration-300 ${
                  currentPage === page
                    ? 'bg-designColor text-white'
                    : 'bg-[#1e2024] text-gray-300 hover:bg-gray-700'
                }`}
              >
                {page}
              </button>
            ))}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === pagination.last_page}
            className={`px-4 py-2 rounded-lg transition-all duration-300 ${
              currentPage === pagination.last_page
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-[#1e2024] text-gray-300 hover:bg-designColor hover:text-white'
            }`}
          >
            {t("pagination.next") || "→"}
          </button>
        </div>
      )}
    </section>
  );
};

export default Projects;